import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { CheckCircle2, ChevronUp, Circle, PartyPopper, Rocket, Star } from 'lucide-react'
import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import { useOwnerOnboardingSteps } from '../hooks/useOwnerOnboardingSteps'
import { useAgentOnboardingSteps } from '../hooks/useAgentOnboardingSteps'
import type { OnboardingStep } from '../lib/steps'

// ─── Tone helpers ────────────────────────────────────────────────────────────

function progressTone(pct: number) {
  if (pct < 34) {
    return {
      indicator: 'bg-amber-500',
      icon: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-400',
      badge: 'bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
      bullet: 'bg-amber-500',
    }
  }
  if (pct < 67) {
    return {
      indicator: 'bg-sky-500',
      icon: 'bg-sky-50 text-sky-600 dark:bg-sky-500/15 dark:text-sky-400',
      badge: 'bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300',
      bullet: 'bg-sky-500',
    }
  }
  return {
    indicator: 'bg-emerald-500',
    icon: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400',
    badge: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300',
    bullet: 'bg-emerald-500',
  }
}

// ─── Card (presentational) ───────────────────────────────────────────────────

interface CardProps {
  icon: React.ReactNode
  steps: (Omit<OnboardingStep, 'isComplete'> & { completed: boolean })[]
  completedCount: number
  totalCount: number
  isLoading: boolean
  collapsed: boolean
  tooltipLabel: string
  onDismiss?: () => void
}

function OnboardingCardBase({
  icon,
  steps,
  completedCount,
  totalCount,
  isLoading,
  collapsed,
  tooltipLabel,
  onDismiss,
}: CardProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [open, setOpen] = useState(true)

  if (isLoading) return null

  const allComplete = completedCount === totalCount
  const progress = Math.round((completedCount / totalCount) * 100)
  const tone = progressTone(progress)
  const nextStep = steps.find((s) => !s.completed)

  if (collapsed) {
    return (
      <TooltipProvider delayDuration={100}>
        <div className="flex justify-center p-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => allComplete ? onDismiss?.() : navigate(nextStep?.route ?? '/dashboard')}
                className={cn(
                  'relative flex h-9 w-9 shrink-0 items-center justify-center rounded-lg transition-opacity hover:opacity-80',
                  tone.icon,
                )}
              >
                {icon}
                {!allComplete && (
                  <span
                    className={cn(
                      'absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full text-[9px] font-bold ring-1 ring-background text-white',
                      tone.bullet,
                    )}
                  >
                    {totalCount - completedCount}
                  </span>
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent side="right">
              {allComplete ? t('onboarding.allComplete') : `${tooltipLabel} — ${completedCount}/${totalCount}`}
            </TooltipContent>
          </Tooltip>
        </div>
      </TooltipProvider>
    )
  }

  return (
    <div className="px-2 pb-3">
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="rounded-[var(--radius)] bg-card shadow-sm">

          {/* Header row */}
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="flex w-full items-center gap-2.5 rounded-t-[var(--radius)] px-3 py-2.5 text-left transition-colors hover:bg-accent/40"
            >
              <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', tone.icon)}>
                {icon}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold text-foreground">
                  {t('onboarding.title')}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {t('onboarding.progress', { completed: completedCount, total: totalCount })}
                </p>
              </div>
              <span className={cn('shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-semibold', tone.badge)}>
                {progress}%
              </span>
              <ChevronUp
                className={cn(
                  'h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200',
                  !open && 'rotate-180',
                )}
              />
            </button>
          </CollapsibleTrigger>

          {/* Progress bar */}
          <div className="px-3 pb-1.5">
            <Progress value={progress} className="h-1" indicatorClassName={tone.indicator} />
          </div>

          {/* Steps list */}
          <CollapsibleContent>
            <TooltipProvider delayDuration={300}>
              <ul className="px-2 pb-2 pt-0.5 space-y-0.5">
                {steps.map((step, idx) => {
                  const isNext = step === nextStep
                  const label = t(`${step.i18nKey}.label`)
                  return (
                    <li key={step.id}>
                      <button
                        type="button"
                        onClick={() => !step.completed && navigate(step.route)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-xs transition-colors',
                          step.completed
                            ? 'cursor-default opacity-45'
                            : isNext
                              ? 'bg-accent/60 font-medium text-foreground hover:bg-accent'
                              : 'text-muted-foreground hover:bg-accent/40 hover:text-foreground',
                        )}
                      >
                        {step.completed ? (
                          <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-emerald-500" />
                        ) : isNext ? (
                          <span className={cn('flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full text-[8px] font-bold text-white', tone.bullet)}>
                            {idx + 1}
                          </span>
                        ) : (
                          <Circle className="h-3.5 w-3.5 shrink-0 text-muted-foreground/40" />
                        )}

                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className={cn('flex-1 truncate', step.completed && 'line-through')}>
                              {label}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent side="right" sideOffset={4}>
                            {label}
                          </TooltipContent>
                        </Tooltip>

                        {!step.completed && !isNext && (
                          <span className="ml-auto shrink-0 text-[9px] tabular-nums text-muted-foreground/50">
                            {idx + 1}
                          </span>
                        )}
                      </button>
                    </li>
                  )
                })}
              </ul>
              {allComplete && onDismiss && (
                <div className="px-2 pb-2">
                  <Button size="sm" className="w-full gap-1.5 text-xs" onClick={onDismiss}>
                    <PartyPopper className="h-3.5 w-3.5" />
                    {t('onboarding.markAsComplete')}
                  </Button>
                </div>
              )}
            </TooltipProvider>
          </CollapsibleContent>
        </div>
      </Collapsible>
    </div>
  )
}

// ─── Public exports ──────────────────────────────────────────────────────────

export function OwnerOnboardingCard({ collapsed = false }: { collapsed?: boolean }) {
  const { steps, completedCount, totalCount, isLoading } = useOwnerOnboardingSteps()
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem('onboarding-owner-dismissed') === 'true',
  )

  const handleDismiss = () => {
    localStorage.setItem('onboarding-owner-dismissed', 'true')
    setDismissed(true)
  }

  if (dismissed) return null

  return (
    <OnboardingCardBase
      icon={<Rocket className="h-3.5 w-3.5" />}
      steps={steps}
      completedCount={completedCount}
      totalCount={totalCount}
      isLoading={isLoading}
      collapsed={collapsed}
      tooltipLabel={t('onboarding.title')}
      onDismiss={handleDismiss}
    />
  )
}

export function AgentOnboardingCard({ collapsed = false }: { collapsed?: boolean }) {
  const { steps, completedCount, totalCount, isLoading } = useAgentOnboardingSteps()
  const { t } = useTranslation()
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem('onboarding-agent-dismissed') === 'true',
  )

  const handleDismiss = () => {
    localStorage.setItem('onboarding-agent-dismissed', 'true')
    setDismissed(true)
  }

  if (dismissed) return null

  return (
    <OnboardingCardBase
      icon={<Star className="h-3.5 w-3.5" />}
      steps={steps}
      completedCount={completedCount}
      totalCount={totalCount}
      isLoading={isLoading}
      collapsed={collapsed}
      tooltipLabel={t('onboarding.title')}
      onDismiss={handleDismiss}
    />
  )
}
